import React, { useState, useEffect, useRef } from 'react';
import { Repeat, Pencil } from '@phosphor-icons/react';
import classNames from 'classnames';
import { toast } from 'react-toastify';

import {
  trackEditProject,
  trackUpdateNameSuccess,
  trackUpdateNameError,
} from '../../../../analytics';

import { Button, ButtonLink, Badge } from '../../../../components/general';

import { URL } from '../../../../constants';

import { updateEvent } from '../../../../services/api/events';

import { useProjectStore } from '../../../../stores/project';

import { getUrl } from '../../../../utils';

interface Props {
  projectName: string;
  eventId: string;
  isSample?: boolean;
  resetSample?: () => void;
}

const ProjectName: React.FC<Props> = (props) => {
  const { projectName, eventId, isSample, resetSample } = props;

  const { updateCurrentEvent } = useProjectStore();

  const [name, setName] = useState(projectName);
  const saveTimeoutRef = useRef<number | null>(null);

  const handleSave = async (isOnBlur?: boolean) => {
    if (name === projectName) return;

    let newName = name;

    if (!name.trim() && isOnBlur) {
      newName = 'Untitled';
      setName(newName);
    }

    try {
      await updateEvent(eventId, { name: newName });
      await updateCurrentEvent();
      trackUpdateNameSuccess();
    } catch (err) {
      setName(projectName);
      trackUpdateNameError();
      toast.error('Failed to update project name. Please try again later.');
    }
  };

  // auto-save after 3 seconds of no typing
  useEffect(() => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);

    saveTimeoutRef.current = window.setTimeout(() => handleSave(), 3000);

    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  const titleContainerClass =
    'px-4 py-1 border border-solid border-transparent rounded-md';
  const titleTextClass =
    'text-[1.75rem] sm:text-[2.5rem] font-medium leading-tight';
  const btnClass =
    'flex items-center justify-center px-2 py-1 gap-1 leading-none rounded-full';

  if (isSample)
    return (
      <React.Fragment>
        <div
          className={classNames(
            titleContainerClass,
            'flex items-center gap-2 flex-wrap',
          )}
        >
          <div className={titleTextClass}>{projectName}</div>

          <Badge
            text="Sample"
            variant="purple-gradient"
            tooltipText="This is a Sample Project, where you can try out CrowdClip® without providing any assets."
          />
        </div>

        <Button
          onClick={resetSample}
          variant="custom"
          colour="salmon-outline-filled"
          className={btnClass}
        >
          <Repeat /> start again
        </Button>
      </React.Fragment>
    );

  return (
    <React.Fragment>
      <input
        name="projectName"
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={() => handleSave(true)}
        className={classNames(
          titleContainerClass,
          titleTextClass,
          'hover:border-gray-200 focus:ring-0 focus:border-grey-900',
        )}
        style={{ fieldSizing: 'content' }}
      />

      <ButtonLink
        to={getUrl(URL.PROJECT_SETTINGS, eventId)}
        onClick={() => trackEditProject('Video Builder')}
        variant="custom"
        colour="salmon-outline-filled"
        className={btnClass}
      >
        <Pencil /> edit
      </ButtonLink>
    </React.Fragment>
  );
};

export default ProjectName;
